import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from '../../../../../components/ui/Dialog';
import Button from '../../../../../components/ui/Button';
import Input from '../../../../../components/ui/Input';
import { markSettlementPaid } from '../../../../../api/Payroll/payroll';
import { formatNumber } from '../../../../../utils';

type Props = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  amount: number;
  currencyLabel: string;
  onSuccess: () => void;
}>;

const MarkSettlementPaidDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  userId,
  amount,
  currencyLabel,
  onSuccess,
}) => {
  const { t } = useTranslation('common');
  const [paymentReference, setPaymentReference] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setPaymentReference('');
      setReason('');
      setSubmitting(false);
      setError('');
    }
  }, [open]);

  // During submit, lock every dismissal path together (X, backdrop, Esc, Cancel).
  const dismissable = !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const result = await markSettlementPaid({
        userId,
        paymentReference: paymentReference.trim() || undefined,
        reason: reason.trim() || undefined,
      });
      if (result.errors?.length) {
        setError(result.errors[0]?.description ?? t('Failed to mark settlement as paid'));
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t('An unexpected error occurred'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !dismissable) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="sm"
        hideCloseButton={!dismissable}
        onEscapeKeyDown={(e) => { if (!dismissable) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (!dismissable) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{t('Record this settlement as paid')}</DialogTitle>
          <DialogDescription>
            {t('Once marked as paid the settlement is finalised and cannot be reopened. Add the payment reference so it can be reconciled later.')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-5">
            <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {t('Net settlement payable')}
              </div>
              <div className="mt-1 text-lg font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatNumber(amount)} {currencyLabel}
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="settlement-payment-reference" className="text-sm font-medium text-foreground">
                {t('Payment reference')}
                <span className="ms-1 text-xs font-normal text-muted-foreground">({t('Optional')})</span>
              </label>
              <Input
                id="settlement-payment-reference"
                value={paymentReference}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPaymentReference(e.target.value)}
                placeholder={t('e.g. bank transfer or cheque number')}
                disabled={submitting}
                maxLength={128}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="settlement-paid-note" className="text-sm font-medium text-foreground">
                {t('Notes')}
                <span className="ms-1 text-xs font-normal text-muted-foreground">({t('Optional')})</span>
              </label>
              <textarea
                id="settlement-paid-note"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                disabled={submitting}
                maxLength={500}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:opacity-50"
              />
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert">{error}</p>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              {t('Cancel')}
            </Button>
            <Button type="submit" variant="default" loading={submitting} disabled={submitting}>
              {t('Mark as paid')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MarkSettlementPaidDialog;
